/**
 * useScrollFade — attach a scroll-fade affordance (mask-image gradient) to a
 * scroll container, shown only on edges where content is actually clipped.
 *
 * SSOT for the "there is more content here" signal. Any scrollable viewport in
 * the app should call this hook on its scroll element, or use
 * `<ScrollFadeContainer>` which wraps this hook.
 *
 * How it works:
 *   - Adds class `scroll-fade` + data-attrs (`data-scroll-fade-axis`,
 *     `data-scroll-fade-size`) so `scrollFade.css` can target it.
 *   - On scroll + resize, flips `data-fade-top|bottom|left|right="on|off"` to
 *     reflect whether content is clipped at that edge.
 *   - CSS reads those data-attrs and applies a `mask-image: linear-gradient(...)`
 *     — GPU-composited, zero reflow.
 *   - Measurement is `requestAnimationFrame`-debounced (one read per frame).
 *
 * Zero cost when content fits (all `data-fade-*` stay "off" → flat mask → no
 * visual effect). Cleanup on unmount strips every attribute this hook added.
 */

import { useEffect, useRef } from 'react';

export type ScrollFadeAxis = 'y' | 'x' | 'both';
export type ScrollFadeSize = 'sm' | 'md' | 'lg';

export type UseScrollFadeOptions = {
    /** Which axis / axes to watch. Default 'y'. */
    axis?: ScrollFadeAxis;
    /** Fade thickness. Default 'md' (12px). */
    fadeSize?: ScrollFadeSize;
    /** When false, hook is inert — no class, no mask. Useful for lists that
     *  may be empty or below an overflow threshold where a fade is noise. */
    enabled?: boolean;
};

export type UseScrollFadeReturn<T extends HTMLElement> = {
    ref: React.RefObject<T | null>;
};

export function useScrollFade<T extends HTMLElement = HTMLDivElement>(
    options: UseScrollFadeOptions = {},
): UseScrollFadeReturn<T> {
    const { axis = 'y', fadeSize = 'md', enabled = true } = options;
    const ref = useRef<T | null>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el || !enabled) return;

        el.classList.add('scroll-fade');
        el.dataset.scrollFadeAxis = axis;
        el.dataset.scrollFadeSize = fadeSize;

        // Sub-pixel fudge for browsers at non-integer zoom — avoids flicker
        // at scroll extremes (0/0 or end/end).
        const THRESHOLD = 1;

        let pending = false;
        let frameHandle = 0;
        const measure = () => {
            pending = false;
            if (!ref.current) return;
            const node = ref.current;
            const wantsY = axis === 'y' || axis === 'both';
            const wantsX = axis === 'x' || axis === 'both';

            if (wantsY) {
                const { scrollTop, scrollHeight, clientHeight } = node;
                node.dataset.fadeTop = scrollTop > THRESHOLD ? 'on' : 'off';
                node.dataset.fadeBottom =
                    scrollTop + clientHeight < scrollHeight - THRESHOLD
                        ? 'on'
                        : 'off';
            }
            if (wantsX) {
                const { scrollLeft, scrollWidth, clientWidth } = node;
                node.dataset.fadeLeft = scrollLeft > THRESHOLD ? 'on' : 'off';
                node.dataset.fadeRight =
                    scrollLeft + clientWidth < scrollWidth - THRESHOLD
                        ? 'on'
                        : 'off';
            }
        };

        // Debounce via a `pending` flag rather than the rAF handle — keeps
        // the reset/set ordering obvious (pending=true → schedule → measure
        // → pending=false), and plays nicely with synchronous rAF stubs in
        // tests where the handle assignment would race the measure().
        const schedule = () => {
            if (pending) return;
            pending = true;
            frameHandle = requestAnimationFrame(measure);
        };

        // Initial measurement (before any resize/scroll events).
        measure();

        el.addEventListener('scroll', schedule, { passive: true });

        // Observe both the container (for viewport size changes) and its first
        // child (for content size changes, e.g. new messages streaming in).
        const ro = new ResizeObserver(schedule);
        ro.observe(el);
        const firstChild = el.firstElementChild;
        if (firstChild) ro.observe(firstChild);

        return () => {
            if (frameHandle !== 0) cancelAnimationFrame(frameHandle);
            el.removeEventListener('scroll', schedule);
            ro.disconnect();
            el.classList.remove('scroll-fade');
            delete el.dataset.scrollFadeAxis;
            delete el.dataset.scrollFadeSize;
            delete el.dataset.fadeTop;
            delete el.dataset.fadeBottom;
            delete el.dataset.fadeLeft;
            delete el.dataset.fadeRight;
        };
    }, [axis, fadeSize, enabled]);

    return { ref };
}
